import { hashKey, useQueryClient, type Query, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { useCallback } from 'react';
import { campaignKey, DEFINITION, type Defined } from './definition';

/** Pass instead of params to address every cached entry of a definition. */
export const every: unique symbol = Symbol('every');

/** One cache edit addressed to a read definition: one params' entry, or every cached entry of it. */
export interface Edit {
  readonly definition: number;
  /** The key after the campaign id, or `every`. */
  readonly key: readonly unknown[] | typeof every;
  readonly change: (data: unknown) => unknown;
}

/**
 * Change what a defined read holds. Only a defined read can be edited, so its key always
 * carries the campaign the write is pinned to.
 */
export function edit<P, D>(query: Defined<P, D>, params: P | typeof every, change: (data: D) => D): Edit {
  const { id, key } = query[DEFINITION];
  return { definition: id, key: params === every ? every : key(params as P), change: change as (data: unknown) => unknown };
}

/** One cache entry a write changed: what it held before and what the write put there. */
export interface Applied {
  readonly key: QueryKey;
  readonly before: unknown;
  readonly after: unknown;
}

function targets(client: QueryClient, campaignId: string | null, e: Edit): Query[] {
  const cache = client.getQueryCache();
  if (e.key === every) {
    return cache.findAll({ predicate: (query) => query.meta?.definition === e.definition && query.queryKey[0] === campaignId });
  }
  const query = cache.find({ queryKey: campaignKey(campaignId, e.key), exact: true });
  return query ? [query] : [];
}

/**
 * Put the edits' optimistic state into the cache, cancelling a fetch in flight for an entry
 * first so it cannot land over the patch. Returns a record per entry for the rollback.
 */
export async function applyEdits(client: QueryClient, campaignId: string | null, edits: readonly Edit[]): Promise<Applied[]> {
  const applied: Applied[] = [];
  for (const e of edits) {
    for (const query of targets(client, campaignId, e)) {
      if (query.state.data === undefined) continue;
      await client.cancelQueries({ queryKey: query.queryKey, exact: true });
      const before = query.state.data;
      const after = client.setQueryData(query.queryKey, e.change(before));
      if (after !== undefined) applied.push({ key: query.queryKey, before, after });
    }
  }
  return applied;
}

/**
 * Undo a failed write's patches. An entry is restored only while it still holds this write's
 * own state; one a refetch or a later write replaced is left as it is and invalidated. A restored
 * entry is invalidated too when `serverMayDiffer`: a lost race or a write that got no answer.
 * Returns the hashes of the entries it invalidated.
 */
export function restoreApplied(client: QueryClient, applied: readonly Applied[], serverMayDiffer: boolean): Set<string> {
  const refreshed = new Set<string>();
  for (const { key, before, after } of [...applied].reverse()) {
    const ours = client.getQueryData(key) === after;
    if (ours) client.setQueryData(key, before);
    if (!ours || serverMayDiffer) {
      refreshed.add(hashKey(key));
      void client.invalidateQueries({ queryKey: key, exact: true });
    }
  }
  return refreshed;
}

/**
 * After a write succeeded: an entry still showing the patch, with no refetch on the way, is one
 * the server's write set missed, so it is invalidated.
 */
export function settleApplied(client: QueryClient, applied: readonly Applied[]): void {
  for (const { key, after } of applied) {
    const state = client.getQueryState(key);
    if (state && state.data === after && state.fetchStatus !== 'fetching') void client.invalidateQueries({ queryKey: key, exact: true });
  }
}

/** Put a response's state into the cache, skipping an entry updated after the response arrived. */
export function landEdits(client: QueryClient, campaignId: string | null, edits: readonly Edit[], responseAt: number): void {
  for (const e of edits) {
    for (const query of targets(client, campaignId, e)) {
      if (query.state.data === undefined || query.state.dataUpdatedAt > responseAt) continue;
      client.setQueryData(query.queryKey, e.change(query.state.data));
    }
  }
}

/**
 * Mark a defined read stale, for a write the server's write set cannot name (a non-API store,
 * the mobile outbox drain). Every campaign's entry of it, since a refresh is cheap and an
 * inactive one only refetches when next observed. Without `restart`, an entry already fetching
 * keeps that fetch instead of starting another.
 */
export function refresh<P>(
  client: QueryClient,
  query: Defined<P, unknown>,
  params: P | typeof every,
  { restart = true }: { restart?: boolean } = {},
): Promise<void> {
  const { id, key } = query[DEFINITION];
  const wanted = params === every ? null : hashKey(key(params as P));
  return client.invalidateQueries(
    { predicate: (q) => q.meta?.definition === id && (wanted === null || hashKey(q.queryKey.slice(1)) === wanted) },
    { cancelRefetch: restart },
  );
}

/** Refresh a defined read; the form a write's `onSuccess` is handed. */
export type Refresh = <P>(query: Defined<P, unknown>, params: P | typeof every) => Promise<void>;

/** `refresh` for a component (a Retry button) that holds no QueryClient. The function is stable across renders. */
export function useRefresh(): Refresh {
  const client = useQueryClient();
  return useCallback((query, params) => refresh(client, query, params), [client]);
}
